import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  ApprovalGroupForSelectFragment,
  ApprovalsGroupFragment,
  MoveApprovalGroupMembershipsDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { ApprovalGroupSelector } from 'modules/approvals/components/ApprovalGroupSelector'

interface Props {
  selectedMembershipIds: string[]
  onClose: () => void
  currentApprovalGroup: ApprovalsGroupFragment
}

const AssignModal = ({ selectedMembershipIds, onClose, currentApprovalGroup }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [destinationGroup, setDestinationGroup] =
    React.useState<ApprovalGroupForSelectFragment | null>(null)

  const [moveApprovalGroupMemberships, { loading }] = useMutation(
    MoveApprovalGroupMembershipsDocument,
    {
      extractorFn: (data) => data.approvalsMutations.bulkMoveApprovalsApprovalGroupMembership,
    }
  )

  const handleSubmit = async () => {
    if (!destinationGroup) return

    const result = await moveApprovalGroupMemberships({
      variables: {
        membershipIds: selectedMembershipIds,
        originApprovalGroupId: currentApprovalGroup.id,
        destinationApprovalGroupId: destinationGroup.id,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      const destinationCategory =
        handled.data?.approvalGroupMemberships?.[0]?.approvalGroup?.category

      if (destinationCategory) {
        navigate(
          navigation.approvals.approvalGroupsCategoryId.details.employees.fullPath({
            id: destinationGroup.id,
            category: destinationCategory,
          })
        )
      }
      onClose()
    }

    return handled
  }

  return (
    <Modal
      title={i18n.t('settings.approvals.approval_group.employees.assign_employees.title_modal')}
      description={i18n.t(
        'settings.approvals.approval_group.employees.assign_employees.description'
      )}
      illustration="user-edit"
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Box alignItems="flexEnd" flexDirection="row" justifyContent="spaceBetween">
          <Button type="primary" disabled={loading || !destinationGroup} submit>
            {i18n.t('settings.approvals.approval_group.employees.assign_employees.title')}
          </Button>
        </Box>
      }
    >
      <Box gap="s24">
        <ApprovalGroupSelector
          excludedIds={[currentApprovalGroup.id]}
          value={destinationGroup}
          onChange={(value) => setDestinationGroup(value)}
          multiple={false}
          label={i18n.t('settings.approvals.approval_group.employees.assign_employees.label')}
          category={currentApprovalGroup.category}
          width="full"
        />
        <Box background="champagne400" padding="s16" borderRadius={{ all: 'abs008' }}>
          <Text.Normal>
            {i18n.t('settings.approvals.approval_group.employees.assign_employees.warning')}
          </Text.Normal>
        </Box>
      </Box>
    </Modal>
  )
}

export default AssignModal
